import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services'
import { STORAGE_KEYS, ROUTE_PATHS } from '../constants/app.constants'
import { decodeJWT, extractRolesFromToken } from '../utils/jwt.utils'
import { useProactiveTokenRefresh } from './useProactiveTokenRefresh'

// Allowed roles for backoffice access
const ALLOWED_ROLES = ['administrator', 'content_admin']

/**
 * Check if user has backoffice access based on JWT token
 * @param token - JWT token string
 * @returns true if user has administrator or content_admin role
 */
const hasBackofficeAccess = (token: string | null): boolean => {
  if (!token) {
    return false
  }

  const roles = extractRolesFromToken(token)
  
  // Check if user has any of the allowed roles
  return roles.some((role) => ALLOWED_ROLES.includes(role))
}

interface AuthContextType {
  user: Record<string, any> | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<Record<string, any> | null>(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA)
    return savedUser ? JSON.parse(savedUser) : null
  })
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  })
  const navigate = useNavigate()

  const login = useCallback(
    async (username: string, password: string) => {
      const response = await authService.login({ username, password })
      // Support both 'token' and 'access_token' for backward compatibility
      const accessToken = response.token || response.access_token

      if (!accessToken) {
        throw {
          message: 'Authentication failed: Missing access token.',
          status: 500,
        }
      }

      // Check roles from token before storing
      if (!hasBackofficeAccess(accessToken)) {
        throw {
          message: 'You do not have permission to access the back office.',
          status: 403,
        }
      }

      // Decode token to get user data
      const decoded = decodeJWT(accessToken)
      if (!decoded?.user) {
        throw {
          message: 'Authentication failed: Invalid token payload.',
          status: 500,
        }
      }

      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken)
      setToken(accessToken)

      // The refresh token is what lets the session outlive the (short) access token —
      // without it stored here, useProactiveTokenRefresh has nothing to exchange and
      // the admin is logged out at the first expiry.
      if (response.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token)
      }

      // Store decoded user from token for consistency
      const userData = decoded.user
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
      setUser(userData)

      navigate(ROUTE_PATHS.DASHBOARD)
    },
    [navigate]
  )

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    // Must go too, or the keepalive would mint a fresh access token for a session the
    // admin just ended.
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    navigate(ROUTE_PATHS.LOGIN)
  }, [navigate])

  // Refresh the access token shortly before it expires, so a short-lived token does not
  // end the session mid-edit. `setToken` is passed so the context reflects the new token
  // rather than holding a stale one — a refresh can legitimately return different roles,
  // which the authorization check below needs to see.
  useProactiveTokenRefresh({ onExpired: logout, onRefreshed: setToken })

  // Check authorization based on token
  const isAuthorized = hasBackofficeAccess(token)

  useEffect(() => {
    if (token && !isAuthorized) {
      logout()
    }
  }, [token, isAuthorized, logout])

  const isAuthenticated = Boolean(token && isAuthorized)

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated,
    }),
    [user, token, login, logout, isAuthenticated]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
